import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { SchemaDriftIssue, groupSchemaDriftIssues } from './schemaDrift';

type Props = {
  issues: SchemaDriftIssue[];
};

const sectionLabels = {
  vendorsettings: 'Vendor settings',
  usersettings: 'User settings',
} as const;

function SchemaDriftPanel({ issues }: Props) {
  if (issues.length === 0) {
    return null;
  }

  const groupedIssues = groupSchemaDriftIssues(issues);
  const propertyLabel = issues.length === 1 ? 'property' : 'properties';
  const verbLabel = issues.length === 1 ? 'is' : 'are';

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mb: 2,
        border: 1,
        borderColor: 'warning.main',
        borderRadius: 1,
        '&::before': {
          display: 'none',
        },
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography fontWeight={600}>Schema drift detected</Typography>
          <Typography color={'text.secondary'} variant={'body2'}>
            {issues.length} unexpected {propertyLabel} {verbLabel} currently present in the stored settings and hidden from the
            form.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography sx={{ mb: 2 }} variant={'body2'}>
          These keys exist in the current settings document but not in the active JSON schema.
        </Typography>
        <Stack spacing={2}>
          {groupedIssues.map((group) => (
            <Box key={group.section}>
              <Typography sx={{ mb: 1 }} variant={'subtitle2'}>
                {sectionLabels[group.section]}
              </Typography>
              <Stack direction={'row'} flexWrap={'wrap'} spacing={1} useFlexGap>
                {group.issues.map((issue) => (
                  <Chip color={'warning'} key={issue.fullPath} label={issue.relativePath} size={'small'} variant={'outlined'} />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default SchemaDriftPanel;
